import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Clock, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { AuthLayout } from '../components/AuthLayout';
import { safeReturnPath } from '../return-url';
import styles from './auth-forms.module.css';

/**
 * AUTH-07 and AUTH-08 — the two screens that report a state rather than ask for
 * input. Both carry the return URL forward, so signing in again lands the user back
 * where they were rather than on a dashboard.
 */

export function SessionExpiredPage(): ReactNode {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { next?: string };
  const next = safeReturnPath(search.next);

  return (
    <AuthLayout
      title="Your session ended"
      tone="warning"
      icon={<Clock size={20} aria-hidden />}
      // §L26 standard message, word for word.
      subtitle="Sign in to continue where you left off."
    >
      <p className={styles.meta}>
        Sessions end after a long gap, or when you sign out somewhere else. Nothing you saved
        has been lost.
      </p>
      <Button
        variant="solid"
        size="lg"
        block
        onClick={() => void navigate({ to: '/login', search: { next } })}
      >
        Sign in again
      </Button>
    </AuthLayout>
  );
}

export function AccountLockedPage(): ReactNode {
  return (
    <AuthLayout
      title="This account is locked"
      tone="danger"
      icon={<Lock size={20} aria-hidden />}
      subtitle="Five wrong passwords in a row locks an account for 30 minutes."
      footer={
        <Link to="/login" className={styles.link}>
          Back to sign in
        </Link>
      }
    >
      <p className={styles.meta}>
        You have two ways forward:
      </p>
      <ul className={styles.list}>
        <li>Wait 30 minutes and try again.</li>
        <li>Reset your password — that clears the lock straight away.</li>
      </ul>
      <p className={styles.meta}>
        If it was not you trying to sign in, tell your admin. Nobody got in.
      </p>
      <Link to="/forgot-password" className={styles.link}>
        Reset my password
      </Link>
    </AuthLayout>
  );
}
